// React
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
// Third-Party
import { Box, Palette, Stack } from "@mui/material";

// Internal
import { ChatMessage } from "./ChatMessage";
// Types
import { IChatMessage } from "@pages/askDost/types";

interface ChatMessagesProps {
  messages: IChatMessage[];
  palette: Palette;
  translate: (text: string, option?: any) => string;
  onRegenerate?: (messageId: string) => void;
  onEditInstructions?: () => void;
  onSendMessage?: (message: string) => void;
  isStreaming?: boolean;
}

export interface ChatMessagesHandle {
  scrollToBottom: () => void;
}

export const ChatMessages = forwardRef<ChatMessagesHandle, ChatMessagesProps>(
  (
    {
      messages,
      palette,
      translate,
      onRegenerate,
      onEditInstructions,
      onSendMessage,
      isStreaming,
    },
    ref,
  ) => {
    const endOfMessagesRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const isUserScrolling = useRef(false);

    useImperativeHandle(ref, () => ({
      scrollToBottom: () => {
        endOfMessagesRef.current?.scrollIntoView({ behavior: "auto" });
        isUserScrolling.current = false;
      },
    }));

    useEffect(() => {
      if (!isUserScrolling.current) {
        endOfMessagesRef.current?.scrollIntoView({ behavior: "auto" });
      }
    }, [messages]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      const element = e.currentTarget;
      const isAtBottom =
        element.scrollHeight - element.scrollTop - element.clientHeight < 100;
      isUserScrolling.current = !isAtBottom;
    };

    return (
      <Stack
        ref={scrollContainerRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "auto",
          width: "100%",
          backgroundColor:
            palette.mode === "dark"
              ? palette.grey[900]
              : palette.background.default,
        }}
      >

        <Box sx={{ width: "100%", maxWidth: 800, mx: "auto", px: 3 }}>
          {messages.map((message, index) => (
            <ChatMessage
              key={message.id}
              message={message}
              palette={palette}
              translate={translate}
              onRegenerate={onRegenerate}
              onEditInstructions={onEditInstructions}
              onSendMessage={onSendMessage}
              isRegeneration={
                index === messages.length - 1 &&
                message.sender === "ai" &&
                !message.content &&
                message.blocks?.length === 0
              }
              isStreaming={
                !!isStreaming &&
                index === messages.length - 1 &&
                message.sender === "ai"
              }
            />
          ))}
          <div ref={endOfMessagesRef} />
        </Box>
      </Stack>
    );
  },
);
